import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import {
  AHclimOpModeEnum,
  AHevchStrategyEnum,
  AHscanDevicesEnum,
  AzzurroHubDataType,
  CmdAzzurroHubService,
  isNotNullOrUndefined,
  System,
} from 'ngx-core-services';
import { ToastFacade, UiPromiseService } from 'ngx-zcs-components';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { selectedSystemAzzurroHubData, SystemsState } from '../../store';

const HEATING_TEMP_VALIDATORS = [Validators.min(30), Validators.max(80)];
const COOLING_TEMP_VALIDATORS = [Validators.min(5), Validators.max(30)];
const DHW_TEMP_VALIDATORS = [Validators.min(25), Validators.max(75)];

const getLabels = () => ({
  title: 'Heat Pump Configuration',
  description: 'Manage the heat pump power and operating parameters, including heating, cooling, and domestic hot water.',
  saving: 'Saving heat pump configuration...',
  saveSuccess: 'Heat pump configuration saved successfully.',
  saveError: 'Error saving heat pump configuration. Please try again.',
  loading: 'Loading heat pump configuration...',
  save: 'Save configuration',
  reset: 'Reset',

  heatPumpSelectorTitle: 'Select heat pump',
  heatPumpSelectorPlaceholder: 'Choose serial number',

  dhwTitle: 'Domestic hot water',
  dhwDescription: 'Enable DHW heating; when enabled, set the target hot water temperature.',
  dhwToggleLabel: 'Domestic hot water',
  dhwSetTempTitle: 'Target temperature (°C)',

  climateTitle: 'Climate control',
  climateDescription: 'Set the climate operating mode (heating or cooling) and the reference temperature for the active circuit.',
  climateToggleLabel: 'Climate control',
  climateModeTitle: 'Operating mode',
  climateModePlaceholder: 'Select mode',
  climateModeCoolingLabel: 'Cooling',
  climateModeHeatingLabel: 'Heating',

  heatingTempTitle: 'Heating temperature (°C)',
  coolingTempTitle: 'Cooling temperature (°C)',

  heatingTempValidatorsError: 'Heating temperature must be between 30°C and 80°C.',
  coolingTempValidatorsError: 'Cooling temperature must be between 5°C and 30°C.',
  dhwTempValidatorsError: 'DHW temperature must be between 25°C and 75°C.',
  genericRequiredFieldError: 'This field is required.',


  startRescan: 'Start Rescan',
  rescanningMessage: 'Azzurro Hub is rescanning devices, please wait...',
  rescanDisclaimerTitle: 'Azzurro Hub Rescan',
  rescanDisclaimerText: 'This will rescan the Azzurro Hub and update the device list. Please ensure that the Azzurro Hub is connected and operational.',
  noHeatpumpWarning: 'No heat pumps have been identified for this system. If a heat pump is already installed, perform a new device scan.',
  moreInfoPrefix: 'Visit our',
  moreInfoLink: 'website',
  moreInfoSuffix: ' for more information about Azzurro heat pumps.',
  orLabel: 'or',
});

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({ 'breakpoint-550': 550 })
@Component({
  selector: 'lib-heatpump-config',
  templateUrl: './heatpump-config.component.html',
  styleUrls: ['./heatpump-config.component.scss'],
})
export class HeatpumpConfigComponent implements IReduxBlock, ICustomBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  heatPumpId$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  readonly azzurroHubData$: Observable<Partial<AzzurroHubDataType>> = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemAzzurroHubData),
  ]).pipe(
    map(([_, data]) => data),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  heatPumps$ = this.store.select(selectedSystemAzzurroHubData).pipe(
    isNotNullOrUndefined(),
    map((data) => data?.heatPump ?? []),
    tap((list) => {
      if (list.length === 1) {
        const only = list[0]?.sn;
        if (only && this.heatPumpId$.value !== only) {
          this.heatPumpId$.next(only);
        }
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isConnected$: Observable<boolean> = this.azzurroHubData$.pipe(
    isNotNullOrUndefined(),
    map((data) => data.info?.connection?.status === 'connected'),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  azzurroHubScanInProgress: boolean = false;

  readonly isRescanning$: Observable<boolean> = this.azzurroHubData$.pipe(
    isNotNullOrUndefined(),
    map(data => data.config?.scanDevices === AHscanDevicesEnum.inProgress),
    tap(isInProgress => {
      if (isInProgress) {
        if (this.azzurroHubScanInProgress) {
          return;
        }
        this.uiPromiseService.run(() => new Promise<void>(() => {}), {
          loading: true,
          loadingMessage: 'Scan devices',
        });
        this.azzurroHubScanInProgress = true;
      } else {
        this.azzurroHubScanInProgress = false;
        this.uiPromiseService.hideAll();
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly canRescan$: Observable<boolean> = combineLatest([
    this.isRescanning$,
    this.isConnected$,
  ]).pipe(
    map(([isRescanning, isConnected]) => !isRescanning && isConnected),
    shareReplay({ bufferSize: 1, refCount: true })
  );



  get hubId(): string | undefined {
    return this.system$.value?.configuration?.azzurroHubId;
  }

  readonly _instanceId$: Observable<string> = new Observable<string>();

  form = new FormGroup({
    DHWOn: new FormControl(true),
    DHWSetTemp: new FormControl<number | null>(null, DHW_TEMP_VALIDATORS),
    climOnA: new FormControl(true),
    climOpMode: new FormControl<AHclimOpModeEnum | null>(null),
    climSetTempHeatingA: new FormControl<number | null>(null, HEATING_TEMP_VALIDATORS),
    climSetTempCoolingA: new FormControl<number | null>(null, COOLING_TEMP_VALIDATORS),
  });

  get dhwOnCtrl() {
    return this.form.get('DHWOn');
  }

  get dhwSetTempCtrl() {
    return this.form.get('DHWSetTemp');
  }

  get climOnACtrl() {
    return this.form.get('climOnA');
  }

  get climOpModeCtrl() {
    return this.form.get('climOpMode');
  }

  get climSetTempHeatingACtrl() {
    return this.form.get('climSetTempHeatingA');
  }

  get climSetTempCoolingACtrl() {
    return this.form.get('climSetTempCoolingA');
  }

  readonly config$ = combineLatest([
    this.store.select(selectedSystemAzzurroHubData).pipe(isNotNullOrUndefined()),
    this.heatPumpId$
  ]).pipe(
    map(([data, selectedId]) => {
      const list = data?.heatPump ?? [];
      const effectiveId = selectedId ?? (list.length === 1 ? list[0]?.sn : undefined);
      return list.find((hp) => hp.sn === effectiveId);
    }),
    isNotNullOrUndefined(),
    tap((config) => {
      if (config) {
        this.form.patchValue({
          DHWOn: config.DHWOn,
          DHWSetTemp: config.DHWSetTemp,
          climOnA: config.climOnA,
          climOpMode: config.climOpMode,
          climSetTempHeatingA: config.climSetTempHeatingA,
          climSetTempCoolingA: config.climSetTempCoolingA,
        });
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  AHevchStrategyEnum = AHevchStrategyEnum;
  AHclimOpModeEnum = AHclimOpModeEnum;

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    private readonly cmdAzzurroHubService: CmdAzzurroHubService,
    private readonly toastFacade: ToastFacade,
    private readonly uiPromiseService: UiPromiseService,
  ) {
    this.setupConditionalValidators();
  }

  async onSave() {
    if (this.form.invalid || !this.hubId) {
      return;
    }

    const config = {
      DHWOn: this.form.value.DHWOn!,
      DHWSetTemp: this.form.value.DHWSetTemp!,
      climOnA: this.form.value.climOnA!,
      climOpMode: this.form.value.climOpMode!,
      climSetTempHeatingA: this.form.value.climSetTempHeatingA!,
      climSetTempCoolingA: this.form.value.climSetTempCoolingA!,
      sn: this.heatPumpId$.value!,
    };

    try {
      const result = await this.cmdAzzurroHubService.configureAzzurroHubHeatPump(
        this.hubId,
        config,
        this.labels.saving
      );

      if (result.error) {
        await this.toastFacade.present(this.labels.saveError, undefined, {
          severity: 'error',
          mobileMode: 'single',
          browserMode: 'simple'
        });
      } else if (result.success) {
        await this.toastFacade.present(this.labels.saveSuccess, undefined, {
          severity: 'success',
          mobileMode: 'single',
          browserMode: 'simple'
        });
      }
    } catch (error) {
      await this.toastFacade.present(this.labels.saveError, undefined, {
        severity: 'error',
        mobileMode: 'single',
        browserMode: 'simple'
      });
    }
  }

  async loadConfiguration() {
    const currentConfig = await this.config$.pipe(take(1)).toPromise();
    if (!currentConfig) {
      return;
    }
    this.form.patchValue({
      DHWOn: currentConfig.DHWOn,
      DHWSetTemp: currentConfig.DHWSetTemp,
      climOnA: currentConfig.climOnA,
      climOpMode: currentConfig.climOpMode,
      climSetTempHeatingA: currentConfig.climSetTempHeatingA,
      climSetTempCoolingA: currentConfig.climSetTempCoolingA,
    });
  }

  async onReset() {
    await this.loadConfiguration();
  }

  private setupConditionalValidators() {
    const dhwOnCtrl = this.dhwOnCtrl;
    const dhwTempCtrl = this.dhwSetTempCtrl;

    if (dhwOnCtrl && dhwTempCtrl) {
      const applyDhwValidators = (on: boolean) => {
        if (on) {
          dhwTempCtrl.setValidators([...DHW_TEMP_VALIDATORS, Validators.required]);
        } else {
          dhwTempCtrl.setValidators(DHW_TEMP_VALIDATORS);
        }
        dhwTempCtrl.updateValueAndValidity({ emitEvent: false });
      };

      // initial state
      applyDhwValidators(!!dhwOnCtrl.value);

      dhwOnCtrl.valueChanges.subscribe((on) => {
        applyDhwValidators(!!on);
      });
    }

    const climOnCtrl = this.climOnACtrl;
    const climModeCtrl = this.climOpModeCtrl;
    const climHeatCtrl = this.climSetTempHeatingACtrl;
    const climCoolCtrl = this.climSetTempCoolingACtrl;

    if (climOnCtrl && climModeCtrl && climHeatCtrl && climCoolCtrl) {
      const applyClimateValidators = (on: boolean) => {
        if (on) {
          climModeCtrl.setValidators([Validators.required]);
          climHeatCtrl.setValidators([...HEATING_TEMP_VALIDATORS, Validators.required]);
          climCoolCtrl.setValidators([...COOLING_TEMP_VALIDATORS, Validators.required]);
        } else {
          climModeCtrl.clearValidators();
          climHeatCtrl.setValidators(HEATING_TEMP_VALIDATORS);
          climCoolCtrl.setValidators(COOLING_TEMP_VALIDATORS);
        }
        climModeCtrl.updateValueAndValidity({ emitEvent: false });
        climHeatCtrl.updateValueAndValidity({ emitEvent: false });
        climCoolCtrl.updateValueAndValidity({ emitEvent: false });
      };

      // initial state
      applyClimateValidators(!!climOnCtrl.value);

      climOnCtrl.valueChanges.subscribe((on) => {
        applyClimateValidators(!!on);
      });
    }
  }
}
